/**
 * TestReadyMeter — visible "you're X% test-ready" progress card.
 *
 * Single GET /api/learner/test-ready call. Designed for the top of LearnerHub.
 * Fails silently (returns null) if the API is unreachable or the user is not a learner.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Target, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TestReadyMeter() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/learner/test-ready`, { withCredentials: true });
        if (!cancelled) setData(data);
      } catch {
        // Quietly hide the meter on error — never block the page.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const pct = Math.max(0, Math.min(100, data.percentage || 0));
  const focus = data.next_focus || {};

  return (
    <section
      data-testid="test-ready-meter"
      className="rounded-2xl bg-gradient-to-br from-amber-50 via-white to-amber-50 dark:from-amber-900/15 dark:via-slate-800 dark:to-amber-900/15 border border-amber-200 dark:border-amber-700/40 p-4 shadow-sm"
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-600" />
          <h3 className="text-xs font-extrabold uppercase tracking-wider text-amber-800 dark:text-amber-200">
            Test-ready meter
          </h3>
        </div>
        <span data-testid="test-ready-pct" className="text-2xl font-extrabold text-slate-900 dark:text-white tabular-nums">
          {Math.round(pct)}<span className="text-sm text-slate-500 font-bold">%</span>
        </span>
      </div>

      {/* Bar */}
      <div className="h-2 rounded-full bg-amber-100 dark:bg-amber-900/30 overflow-hidden mb-3" aria-label={`${Math.round(pct)} percent test ready`}>
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Breakdown chips */}
      <div className="grid grid-cols-2 gap-1.5 mb-3">
        {(data.breakdown || []).map((b) => (
          <div
            key={b.id}
            data-testid={`test-ready-breakdown-${b.id}`}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5"
          >
            <div className="text-[9px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-bold leading-tight">{b.label}</div>
            <div className="text-[13px] font-bold text-slate-900 dark:text-white tabular-nums">
              {b.value}<span className="text-slate-400 font-normal">/{b.total}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Next focus CTA */}
      {focus.title && (
        <button
          data-testid="test-ready-next-focus"
          onClick={() => {
            if (focus.type === "lesson" && focus.id) nav(`/lesson/${focus.id}`);
            else nav("/learn");
          }}
          className="w-full flex items-center justify-between rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 px-3 py-2 text-xs font-extrabold transition active:scale-[0.99]"
        >
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Next focus: {focus.title}
          </span>
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </section>
  );
}
